import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Procurement View — create requests, view offers, compare, decide.
 * Accessed from Dashboard card or chat.
 */
export default function ProcurementView({ token, onClose, onShowDetail }) {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/v1/aif/procurement/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, []);

  const STATUS_COLORS = {
    DRAFT: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    SENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    OFFERS_RECEIVED: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    DECIDED: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    CLOSED: 'bg-gray-100 text-[var(--aif-text-muted)] dark:bg-gray-800 dark:text-gray-400',
  };

  if (selectedRequest) {
    return (
      <OfferComparison
        token={token}
        requestId={selectedRequest}
        onBack={() => { setSelectedRequest(null); fetchRequests(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[var(--aif-text-primary)]">{t('procurement.title')}</h3>
        <button
          onClick={() => setShowForm(true)}
          className="px-3 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium hover:bg-[var(--aif-color-primary-dark)] transition"
        >
          + {t('procurement.newRequest')}
        </button>
      </div>

      {/* New Request Form */}
      {showForm && (
        <ProcurementForm
          token={token}
          onCreated={() => { setShowForm(false); fetchRequests(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Request List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-3xl mb-2">{'\uD83D\uDCE6'}</div>
          <p className="text-sm text-[var(--aif-text-muted)]">{t('procurement.noRequests')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedRequest(r.id)}
              className="w-full text-left bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 hover:bg-[var(--aif-bg-elevated)] transition space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{r.title}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status] || ''}`}>
                  {t(`procurement.status_${r.status}`, r.status)}
                </span>
              </div>
              <div className="text-xs text-[var(--aif-text-muted)]">
                {r.product_name} &middot; {r.quantity} {r.unit}
                {r.offer_count > 0 && <span> &middot; {r.offer_count} {t('procurement.offers')}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Procurement Form ────────────────────────────────────────────────────────

function ProcurementForm({ token, onCreated, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: '', product_name: '', quantity: '', unit: 'kg',
    delivery_date_from: '', delivery_date_to: '', delivery_location: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.title || !form.product_name || !form.quantity) return;
    setSaving(true);
    try {
      await apiClient.post('/api/v1/aif/procurement/requests', { ...form, quantity: parseFloat(form.quantity) }, { queueOnOffline: true });
      onCreated();
    } catch { /* ApiError */ }
    setSaving(false);
  };

  const inputClass = "w-full px-3 py-2 rounded-lg bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-sm text-[var(--aif-text-primary)] placeholder-[var(--aif-text-muted)] focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]/30";

  return (
    <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl p-4 space-y-3">
      <h4 className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('procurement.newRequest')}</h4>
      <input value={form.title} onChange={e => update('title', e.target.value)} placeholder={t('procurement.titlePlaceholder')} className={inputClass} />
      <div className="flex gap-2">
        <input value={form.product_name} onChange={e => update('product_name', e.target.value)} placeholder={t('procurement.product')} className={`flex-1 ${inputClass}`} />
      </div>
      <div className="flex gap-2">
        <input type="number" value={form.quantity} onChange={e => update('quantity', e.target.value)} placeholder={t('procurement.quantity')} className={`flex-1 ${inputClass}`} />
        <select value={form.unit} onChange={e => update('unit', e.target.value)} className={`w-24 ${inputClass}`}>
          <option value="kg">kg</option>
          <option value="t">t</option>
          <option value="l">l</option>
          <option value="Stk">{t('procurement.unitPcs')}</option>
        </select>
      </div>
      <div className="flex gap-2">
        <input type="date" value={form.delivery_date_from} onChange={e => update('delivery_date_from', e.target.value)} className={`flex-1 ${inputClass}`} />
        <input type="date" value={form.delivery_date_to} onChange={e => update('delivery_date_to', e.target.value)} className={`flex-1 ${inputClass}`} />
      </div>
      <input value={form.delivery_location} onChange={e => update('delivery_location', e.target.value)} placeholder={t('procurement.location')} className={inputClass} />
      <textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder={t('procurement.notes')} rows={2} className={`${inputClass} resize-none`} />
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-lg text-sm text-[var(--aif-text-muted)] bg-[var(--aif-bg-elevated)]">{t('procurement.cancel')}</button>
        <button onClick={handleSubmit} disabled={saving || !form.title || !form.product_name || !form.quantity} className="flex-1 py-2 rounded-lg text-sm text-white bg-[var(--aif-color-primary)] font-medium disabled:opacity-50">{saving ? '...' : t('procurement.create')}</button>
      </div>
    </div>
  );
}

// ── Offer Comparison ────────────────────────────────────────────────────────

function OfferComparison({ token, requestId, onBack }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/procurement/requests/${requestId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setData(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [requestId]);

  const handleDecide = async (offerId) => {
    setDeciding(true);
    try {
      await apiClient.post(`/api/v1/aif/procurement/requests/${requestId}/decide`, { offer_id: offerId }, { queueOnOffline: true });
      onBack();
    } catch { /* ApiError */ }
    setDeciding(false);
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-sm text-[var(--aif-text-muted)] py-4">{t('ui.notFound')}</p>;

  const offers = data.offers || [];
  const rec = data.recommendation;

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-xs text-[var(--aif-color-primary)]">&larr; {t('procurement.backToList')}</button>

      <div>
        <h3 className="text-base font-semibold text-[var(--aif-text-primary)]">{data.title}</h3>
        <p className="text-xs text-[var(--aif-text-muted)]">{data.product_name} &middot; {data.quantity} {data.unit}</p>
        {data.offer_count > 0 && (
          <p className="text-xs text-[var(--aif-text-secondary)] mt-1">{data.offer_count} {t('procurement.offersReceived')}</p>
        )}
      </div>

      {/* AI Recommendation */}
      {rec && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm">{'\uD83C\uDFC6'}</span>
            <span className="text-xs font-semibold text-green-700 dark:text-green-300">{t('procurement.aiRecommendation')}</span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400">{rec.reason}</p>
        </div>
      )}

      {/* Offers */}
      {offers.length === 0 ? (
        <p className="text-sm text-[var(--aif-text-muted)] py-4 text-center">{t('procurement.noOffers')}</p>
      ) : (
        <div className="space-y-3">
          {offers.map((o, idx) => {
            const isRecommended = rec && rec.recommended_offer_id === o.id;
            const isDecided = data.decided_offer_id === o.id;
            return (
              <div
                key={o.id}
                className={`bg-[var(--aif-bg-surface)] border rounded-xl p-4 space-y-2 ${
                  isRecommended ? 'border-green-400 ring-1 ring-green-200' : 'border-[var(--aif-border-default)]'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {isRecommended && <span className="text-xs text-green-600 font-medium">{'\uD83C\uDFC6'} {t('procurement.recommended')}</span>}
                    <div className="text-sm font-medium text-[var(--aif-text-primary)]">{o.vendor_company || o.vendor_name}</div>
                  </div>
                  {isDecided && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{t('procurement.chosen')}</span>}
                </div>
                <div className="text-xs text-[var(--aif-text-secondary)] space-y-0.5">
                  {o.price_per_unit && <div>{o.price_per_unit} {t('procurement.currencyPerUnit', { currency: 'EUR', unit: data.unit })} &middot; {o.total_price} EUR</div>}
                  {o.delivery_conditions && <div>{o.delivery_conditions}</div>}
                  {o.delivery_date && <div>{t('procurement.deliveryBy')}: {new Date(o.delivery_date).toLocaleDateString()}</div>}
                  {o.notes && <div className="italic">{o.notes}</div>}
                </div>
                {data.status !== 'DECIDED' && data.status !== 'CLOSED' && (
                  <button
                    onClick={() => handleDecide(o.id)}
                    disabled={deciding}
                    className="px-4 py-1.5 rounded-lg bg-[var(--aif-color-primary)] text-white text-xs font-medium hover:bg-[var(--aif-color-primary-dark)] transition disabled:opacity-50"
                  >
                    {t('procurement.choose')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
